import type { CustomerModel } from "@/models";
import {
    between,
    email,
    helpers,
    maxLength,
    minLength,
    minValue,
    numeric,
    required,
    requiredIf,
} from "@vuelidate/validators";
import dayjs from "dayjs";
import { validDays } from "@/validation";
import { useAddressStore } from "@/stores";

export const validDate = (customer: CustomerModel) => () => {
    if (
        customer.dobYear !== "" &&
        customer.dobMonth !== "" &&
        customer.dobDate !== ""
    ) {
        const date = dayjs(
            `${customer.dobYear}-${customer.dobMonth}-${customer.dobDate}`,
            "YYYY-MM-DD",
            true
        );
        if (
            date.isValid() &&
            validDays(+customer.dobDate, +customer.dobMonth, +customer.dobYear)
        ) {
            return true;
        }
    }
    return false;
};

export const staffIdRule = {
    minLength: helpers.withMessage(
        "Your Staff ID should be no more than 7 digits",
        maxLength(8)
    ),
    staffIdFormat: helpers.withMessage(
        "Invalid staff ID format",
        helpers.regex(/I[\d]{6,7}\b/gi)
    ),
};

export const postcodeRule = {
    isRequired: helpers.withMessage(
        "Please enter a valid UK postcode.",
        required
    ),
    notBlockedPrefix: helpers.withMessage(
        "Please enter a valid UK postcode.",
        (value: string) => {
            if (!value) {
                return true;
            }
            const addressStore = useAddressStore();
            return addressStore.validPostcode(value);
        }
    ),
    postcode: helpers.withMessage(
        "Please enter a valid UK postcode.",
        // eslint-disable-next-line max-len
        helpers.regex(
            /^((([A-Z]{2}[0-9]{1,2})|([A-Z]{1,2}[0-9][A-Z])|([A-Z][0-9]{1,2}))\s?([0-9][A-Z]{2})|(BFPO\s?\d{1,4})|(GIR\s?0AA))$/i
        )
    ),
};

export const ageRule = {
    betweenValue: helpers.withMessage(
        "You must be between the ages of 50 and 80.",
        between(50, 80)
    ),
};

export const dobDateRule = {
    required,
};

export const smokerRule = {
    isRequired: helpers.withMessage(
        "We were unable to determine your smoker status.",
        required
    ),
};

export const dobMonthRule = {
    required,
};

export const dobYearRule = {
    required,
};

export const fullDobRule = (customer: CustomerModel) => {
    return {
        validDate: helpers.withMessage(
            "Please enter a valid date of birth.",
            validDate(customer)
        ),
    };
};

export const dayPhoneRule = (includeRequired = false) => {
    return {
        isRequired: includeRequired
            ? helpers.withMessage(
                  "Please enter a valid telephone number.",
                  required
              )
            : false,
        phoneNumber: helpers.withMessage(
            "Please enter a valid telephone number.",
            helpers.regex(/^[0-9]{10,13}$/i)
        ),
    };
};

export const titleRule = (customer: CustomerModel, includeRequired = false) => {
    return {
        isRequired: helpers.withMessage(
            "Please select your title.",
            includeRequired
                ? required
                : requiredIf(
                      customer.dayPhone !== undefined &&
                          customer.dayPhone.length >= 10
                  )
        ),
    };
};

export const firstnameRule = (
    customer: CustomerModel,
    includeRequired = false
) => {
    return {
        isRequired: helpers.withMessage(
            "Enter your first name.",
            includeRequired
                ? required
                : requiredIf(
                      customer.dayPhone !== undefined &&
                          customer.dayPhone.length >= 10
                  )
        ),
        minLength: minLength(2),
        maxLength: maxLength(25),
        alphaSpaces: helpers.withMessage(
            "Enter your first name in a valid format. Numbers and certain special characters are not accepted.",
            helpers.regex(/^[A-Za-z ]*$/)
        ),
    };
};

export const middlenameRule = {
    minLength: minLength(2),
    maxLength: maxLength(25),
    alphaSpaces: helpers.withMessage(
        "Enter your middle name in a valid format. Numbers and certain special characters are not accepted.",
        helpers.regex(/^[A-Za-z ]*$/)
    ),
};

export const surnameRule = (
    customer: CustomerModel,
    includeRequired = false
) => {
    return {
        isRequired: helpers.withMessage(
            "Enter your surname.",
            includeRequired
                ? required
                : requiredIf(
                      customer.dayPhone !== undefined &&
                          customer.dayPhone.length >= 10
                  )
        ),
        minLength: minLength(2),
        maxLength: maxLength(25),
        alphaSpaces: helpers.withMessage(
            "Enter your surname in a valid format. Numbers and certain special characters are not accepted.",
            helpers.regex(/^[A-Za-z ]*$/)
        ),
    };
};

export const emailRule = {
    isRequired: helpers.withMessage(
        "Please enter a valid email address.",
        required
    ),
    email: helpers.withMessage("Please enter a valid email address.", email),
};

export const selectedAddressRule = {
    isRequired: helpers.withMessage("Please select your address.", required),
};

export const addressLine1Rule = {
    isRequired: helpers.withMessage(
        "You must provide your house name or number.",
        required
    ),
};

export const addressLine2Rule = {};

export const addressLine3Rule = {};

export const addressLine4Rule = {
    isRequired: helpers.withMessage(
        "Please provide a valid town or city.",
        required
    ),
};

export const addressLine5Rule = {};

export const accountNumberRule = {
    isRequired: helpers.withMessage(
        "Please enter a valid account number.",
        required
    ),
    numeric,
    minLength: minLength(8),
    maxLength: maxLength(8),
};

export const sortCodeRule = {
    isRequired: helpers.withMessage(
        "Please enter a valid sort code.",
        required
    ),
    sixDigits: helpers.withMessage(
        "The sort code field must be 6 digits in length.",
        maxLength(8)
    ),
    minLength: helpers.withMessage(
        "The sort code field must be 6 digits in length.",
        minLength(8)
    ),
};

export const bankNameRule = {
    isRequired: helpers.withMessage("Please enter your bank name.", required),
};

export const accountHolderRule = {
    isRequired: helpers.withMessage(
        "Please enter the account holders name.",
        required
    ),
    minLength: minLength(2),
    maxLength: maxLength(25),
    alphaSpaces: helpers.withMessage(
        "Enter your first name in a valid format. Numbers and certain special characters are not accepted.",
        helpers.regex(/^[A-Za-z ]*$/)
    ),
};

export const branchRule = {
    isRequired: helpers.withMessage("Please enter your branch name.", required),
};

export const directDebitDayRule = {
    isRequired: helpers.withMessage(
        "Please select the day you prefer the direct debit to be taken.",
        required
    ),
    numeric,
    minValue: helpers.withMessage(
        "Please select the day you prefer the direct debit to be taken.",
        minValue(1)
    ),
};
